import type { Project } from "../../../engine/src/types.ts";
import { ApiError } from "./api.ts";
import { History } from "./history.ts";

export type SaveStatus = "saved" | "dirty" | "saving" | "offline" | "error" | "conflict";
export type Selection =
  | { kind: "none" } | { kind: "element"; id: string } | { kind: "scene"; id: string }
  | { kind: "keyframe"; trackId: string; kfId: string; elementId: string } | { kind: "audio"; id: string } | { kind: "cue"; id: string };

export interface EditorState {
  project: Project | null; serverRevision: number; selection: Selection; timeMs: number; playing: boolean;
  pxPerSec: number; save: SaveStatus; saveMessage: string; canUndo: boolean; canRedo: boolean;
}
export interface SaveApi { putProject(id: string, baseRevision: number, project: Project): Promise<Project> }
export interface Env {
  api: SaveApi; debounceMs: number; retryMs: number;
  setTimeout: (fn: () => void, ms: number) => unknown; clearTimeout: (h: unknown) => void;
  raf: (fn: (ts: number) => void) => unknown; caf: (h: unknown) => void; now: () => number;
}
export const browserEnv = (api: SaveApi): Env => ({
  api, debounceMs: 800, retryMs: 3000,
  setTimeout: (fn, ms) => setTimeout(fn, ms), clearTimeout: (h) => clearTimeout(h as number),
  raf: (fn) => requestAnimationFrame(fn), caf: (h) => cancelAnimationFrame(h as number), now: () => performance.now(),
});

export class EditorStore {
  private state: EditorState;
  private listeners = new Set<() => void>();
  private history: History<Project> | null = null;
  private saveTimer: unknown = null; private retryTimer: unknown = null; private inflight = false; private retries = 0;
  private rafHandle: unknown = null; private lastTs = 0;
  private lastSaved: Project | null = null;
  private readonly env: Env;

  constructor(env: Env) {
    this.env = env;
    this.state = { project: null, serverRevision: 0, selection: { kind: "none" }, timeMs: 0, playing: false, pxPerSec: 120,
      save: "saved", saveMessage: "", canUndo: false, canRedo: false };
  }
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getState = (): EditorState => this.state;
  private set(patch: Partial<EditorState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of [...this.listeners]) l();
  }
  private syncHistory(): Partial<EditorState> {
    const h = this.history!;
    return { project: h.present, canUndo: h.canUndo, canRedo: h.canRedo };
  }

  load(project: Project): void {
    this.pause(); this.cancelTimers();
    this.history = new History(project); this.lastSaved = project; this.retries = 0;
    this.set({ project, serverRevision: project.revision, selection: { kind: "none" }, timeMs: 0, save: "saved",
      saveMessage: "", canUndo: false, canRedo: false });
  }
  dispose(): void { this.pause(); this.cancelTimers(); this.listeners.clear(); }

  /** Apply an edit. `coalesce` merges rapid successive edits (drag, slider, typing) into one undo step. */
  edit(fn: (p: Project) => Project, coalesce: string | null = null): void {
    if (!this.history) return;
    const next = fn(this.history.present);
    if (next === this.history.present) return;
    this.history.push(next, coalesce, this.env.now());
    this.set({ ...this.syncHistory(), save: this.state.save === "conflict" ? "conflict" : "dirty" });
    this.scheduleSave();
  }
  undo(): void { if (this.history?.canUndo) { this.history.undo(); this.afterHistory(); } }
  redo(): void { if (this.history?.canRedo) { this.history.redo(); this.afterHistory(); } }
  private afterHistory(): void {
    const p = this.history!.present;
    const sel = this.state.selection;
    const keep = sel.kind === "none" || (sel.kind === "element" && p.elements[sel.id]) || (sel.kind === "scene" && p.scenes.some((s) => s.id === sel.id)) || (sel.kind === "audio" && p.audio.some((a) => a.id === sel.id)) || (sel.kind === "cue" && p.subtitles.cues.some((c) => c.id === sel.id))
      || (sel.kind === "keyframe" && p.tracks.some((t) => t.id === sel.trackId && t.keyframes.some((k) => k.id === sel.kfId)));
    this.set({ ...this.syncHistory(), selection: keep ? sel : { kind: "none" }, save: this.state.save === "conflict" ? "conflict" : "dirty" });
    this.scheduleSave();
  }

  select(selection: Selection): void { this.set({ selection }); }
  seek(ms: number): void {
    const d = this.state.project?.duration_ms ?? 0;
    this.set({ timeMs: Math.min(Math.max(0, Math.round(ms)), d) });
  }
  setZoom(pxPerSec: number): void { this.set({ pxPerSec: Math.min(600, Math.max(4, pxPerSec)) }); }

  // ---- playback: ONE time source (this store); rendering and the playhead both read it ----
  play(): void {
    const p = this.state.project;
    if (!p || this.state.playing) return;
    if (this.state.timeMs >= p.duration_ms) this.set({ timeMs: 0 });
    this.lastTs = this.env.now();
    this.set({ playing: true });
    const tick = (): void => {
      if (!this.state.playing || !this.state.project) return;
      const now = this.env.now(), dt = now - this.lastTs; this.lastTs = now;
      const end = this.state.project.duration_ms, t = this.state.timeMs + dt;
      if (t >= end) { this.set({ timeMs: end, playing: false }); this.rafHandle = null; return; }
      this.set({ timeMs: t });
      this.rafHandle = this.env.raf(tick);
    };
    this.rafHandle = this.env.raf(tick);
  }
  pause(): void {
    if (this.rafHandle !== null) this.env.caf(this.rafHandle);
    this.rafHandle = null;
    if (this.state.playing) this.set({ playing: false });
  }

  // ---- autosave: debounced, one request in flight, compare-and-swap on the server revision ----
  private cancelTimers(): void {
    if (this.saveTimer !== null) this.env.clearTimeout(this.saveTimer);
    if (this.retryTimer !== null) this.env.clearTimeout(this.retryTimer);
    this.saveTimer = this.retryTimer = null;
  }
  private scheduleSave(): void {
    if (this.state.save === "conflict") return;
    if (this.saveTimer !== null) this.env.clearTimeout(this.saveTimer);
    this.saveTimer = this.env.setTimeout(() => { this.saveTimer = null; void this.saveNow(); }, this.env.debounceMs);
  }
  get dirty(): boolean { return this.history !== null && this.history.present !== this.lastSaved; }

  async saveNow(): Promise<void> {
    const h = this.history;
    if (!h || this.inflight || this.state.save === "conflict") return;
    if (this.saveTimer !== null) { this.env.clearTimeout(this.saveTimer); this.saveTimer = null; }
    if (h.present === this.lastSaved) { this.set({ save: "saved" }); return; }
    const sent = h.present;
    this.inflight = true;
    this.set({ save: "saving", saveMessage: "" });
    try {
      const res = await this.env.api.putProject(sent.id, this.state.serverRevision, sent);
      this.lastSaved = sent; this.retries = 0;
      this.set({ serverRevision: res.revision });
      if (this.history?.present !== sent) { this.set({ save: "dirty" }); this.scheduleSave(); } else this.set({ save: "saved" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) this.set({ save: "conflict", saveMessage: e.message });
      else if (e instanceof ApiError && e.status === 0) {
        this.retries++;
        this.set({ save: "offline", saveMessage: "" });
        this.retryTimer = this.env.setTimeout(() => { this.retryTimer = null; void this.saveNow(); },
          Math.min(30000, this.env.retryMs * this.retries));
      } else this.set({ save: "error", saveMessage: e instanceof Error ? e.message : String(e) });
    } finally { this.inflight = false; }
  }
}
